import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { VehicleResult } from '../../types';
import { VEHICLE_COLORS } from './TCOSummaryTable';

interface Props {
  vehicles: VehicleResult[];
  // Phase 24.3: optional purchase prices keyed by vehicle index so the tooltip
  // can render residual value as a percentage of the original purchase price.
  // Falls back to month-0 residual when absent — preserves back-compat with
  // any call site that has not been updated yet.
  purchasePrices?: (number | null)[];
}

function buildChartData(vehicles: VehicleResult[]) {
  const paths = vehicles.map((v) => {
    const dep = v.depreciation as Record<string, unknown>;
    const path = dep['monthly_value_path'];
    return Array.isArray(path) ? (path as number[]) : [];
  });
  const maxLen = Math.max(...paths.map((p) => p.length), 0);

  return Array.from({ length: maxLen }, (_, month) => {
    const point: Record<string, number> = { month };
    vehicles.forEach((_v, i) => {
      const val = paths[i][month];
      point[`v${i}`] = typeof val === 'number' ? Math.round(val) : 0;
    });
    return point;
  });
}

function fmtDollar(v: number) {
  if (Math.abs(v) < 100) {
    return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return '$' + Math.round(v).toLocaleString('en-US');
}

export default function DepreciationChart({ vehicles, purchasePrices }: Props) {
  const data = buildChartData(vehicles);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm" style={{ color: 'var(--white-dim)' }}>
        No depreciation data available.
      </div>
    );
  }

  // Phase 24.3: resolve a denominator per vehicle for the percentage display.
  // Prefer the explicit purchase price; otherwise fall back to month-0 residual.
  const denominators = vehicles.map((_v, i) => {
    const explicit = purchasePrices?.[i];
    if (typeof explicit === 'number' && explicit > 0) return explicit;
    const firstPoint = data[0] as Record<string, number>;
    const m0 = firstPoint[`v${i}`];
    return typeof m0 === 'number' && m0 > 0 ? m0 : null;
  });

  return (
    <ResponsiveContainer width="100%" height={600}>
      <LineChart data={data} margin={{ top: 8, right: 24, left: 16, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e2530" />
        <XAxis
          dataKey="month"
          stroke="#8a93a2"
          tick={{ fill: '#8a93a2', fontSize: 14 }}
          label={{ value: 'Month', position: 'insideBottom', offset: -2, fill: '#8a93a2', fontSize: 14 }}
        />
        <YAxis
          stroke="#8a93a2"
          tick={{ fill: '#8a93a2', fontSize: 14 }}
          tickFormatter={fmtDollar}
          width={80}
        />
        <Tooltip
          contentStyle={{ background: '#111418', border: '1px solid #1e2530', borderRadius: 8 }}
          labelStyle={{ color: '#8a93a2', fontSize: 14 }}
          // Phase 24.3: the formatter now emits both the dollar residual and
          // the percentage of purchase price so the Y-axis value is explicit
          // in the tooltip body rather than implied by the line position.
          formatter={(value: number | undefined, name: string | undefined) => {
            if (value === undefined || name === undefined) return ['-', name ?? ''];
            const idx = Number(name.replace('v', ''));
            const denom = denominators[idx];
            const pctStr = denom && denom > 0
              ? ` (${((value / denom) * 100).toFixed(0)}% of purchase price)`
              : '';
            const label = vehicles[idx]?.label ?? name;
            return [`${fmtDollar(value)}${pctStr}`, `Residual Value — ${label}`];
          }}
          labelFormatter={(label) => `Month: ${label}`}
        />
        <Legend
          formatter={(value) => {
            const idx = Number(value.replace('v', ''));
            return vehicles[idx]?.label ?? value;
          }}
          wrapperStyle={{ fontSize: 15, color: '#8a93a2' }}
        />
        {vehicles.map((_, i) => (
          <Line
            key={i}
            type="monotone"
            dataKey={`v${i}`}
            stroke={VEHICLE_COLORS[i]}
            dot={false}
            strokeWidth={2}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
